// src/components/TicketDetailView.tsx
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchTicketById, closeTicket, updateRating } from "../api";
import type { Ticket } from "../types";
import cardStyles from "../styles/Card.module.css";
import buttonStyles from "../styles/Button.module.css";
import { NoteComponent } from "./NoteComponent";
import Icon from '@mdi/react';
import { mdiThumbDownOutline, mdiThumbUpOutline } from '@mdi/js';

export default function TicketDetailView() {
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTicketById(Number(id)).then(setTicket);
  }, [id]);

  if (!ticket) return <p>Loading…</p>;

  const onClose = async () => {
    const updated = await closeTicket(ticket.id);
    setTicket(updated);
  };

  function displayNotes() {
    if (ticket === null || ticket.notes.length === 0) {
      return <div>No Notes</div>;
    }
    return ticket.notes.map((note, i) => <NoteComponent key={i} {...note} />);
  }

  function displayStatus(t:Ticket) {
    if (t.status === "Closed") {
      return <span className={buttonStyles.statusClosed}>Closed</span>
    } else if (t.status === "InProgress" || t.status === "EscalatedToUnify") {
      return <span className={buttonStyles.statusInProgress}>In Progress</span>
    }
    return <span className={buttonStyles.statusOpen}>Open</span>
  }

  function ratingReminder() {
    if (ticket?.status === "Closed" && ticket?.rating == "Unrated") {
      return (
        <div
          style={{
            background: "#0099ff",
            padding: "4px 12px",
            borderRadius: "12px",
            marginBottom: "12px",
            color: "white",
          }}
        >Please answer the question at the bottom of this ticket.</div>
      )
    } else {
      return null
    }
  }

  function displayRating() {
    if (ticket?.status === "Closed" && ticket?.rating === "Unrated") {
      return (
        <div className={cardStyles.card}>
          <h4>Rating</h4>
          <div>Did you have a positive experience with this ticket?</div>
          <button
            className={buttonStyles.statusOpen}
            style={{
              border: "none",
              padding: "8px 16px",
              margin: 8
            }}
            onClick={() => rateTicket("Positive")}
            >
              <Icon path={mdiThumbUpOutline} size={1} />
            </button>
          <button
            className={buttonStyles.statusClosed}
            style={{
              border: "none",
              padding: "8px 16px",
              margin: 8
            }}
            onClick={() => rateTicket("Negative")}
            >
              <Icon path={mdiThumbDownOutline} size={1} />
            </button>
        </div>
      )
    } else {
      return null
    }
  }

  function rateTicket(rating:"Positive" | "Negative") {
    if (ticket == null) {
      return null
    }

    updateRating(ticket.id, rating).then(setTicket)
  }

  return (
    <div className={cardStyles.card}>
      <div>
        {ratingReminder()}
      </div>
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: 16,
          borderRadius: 8,
          border: "none",
          backgroundColor: "#f0f0f0",
          padding: "8px 16px",
          cursor: "pointer",
        }}
      >
        ← Back
      </button>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2>Ticket #{ticket.id}</h2>
        {displayStatus(ticket)}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 24,
        }}
      >
        <div>
          <h4>Fault(s)</h4>
          {ticket.faults.map((f, i) => (
            <p key={i}>{f}</p>
          ))}
        </div>
        <div>
          <h4>User</h4>
          <p>{ticket.userName}</p>
          {ticket.submittedByUser !== ticket.userName && (
            <p style={{ fontStyle: "italic", color: "#888" }}>
              Submitted by: {ticket.submittedByUser}
            </p>
          )}
        </div>
        <div>
          <h4>Cell number</h4>
          <p>{ticket.location}</p>
        </div>
      </div>
      <div>
        <h4>Notes</h4>
        {displayNotes()}
      </div>
      <div>
        {displayRating()}
      </div>
    </div>
  );
}
